import React, { useState, useEffect, useRef } from "react";

const ProjectsTimeline = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeIndex, setActiveIndex] = useState(0);
  
  const itemRefs = useRef([]);
  const sentinelRefs = useRef([]);

  const API_ENDPOINT = "https://portfolio.triodedevs.workers.dev/public/user/tri_m009";

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const response = await fetch(API_ENDPOINT);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const jsonData = await response.json();
        if (jsonData && jsonData.data) {
          const sortedData = jsonData.data.sort((a, b) => a.assignment.order - b.assignment.order);
          setProjects(sortedData);
        } else {
          throw new Error("Data format from API is incorrect.");
        }
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    };
    fetchProjects();
  }, []);

  useEffect(() => {
    if (!sentinelRefs.current.length || loading) return;

    let frame = 0;
    const updateActiveByProximity = () => {
      frame = requestAnimationFrame(updateActiveByProximity);
      const centerY = window.innerHeight / 3;
      let bestIndex = 0;
      let bestDist = Infinity;
      
      sentinelRefs.current.forEach((node, i) => {
        if (!node) return;
        const rect = node.getBoundingClientRect();
        const mid = rect.top + rect.height / 2;
        const dist = Math.abs(mid - centerY);
        if (dist < bestDist) {
          bestDist = dist;
          bestIndex = i;
        }
      });
      
      if (bestIndex !== activeIndex) setActiveIndex(bestIndex);
    };

    frame = requestAnimationFrame(updateActiveByProximity);
    return () => cancelAnimationFrame(frame);
  }, [activeIndex, loading]);

  useEffect(() => {
    if (!loading && projects.length > 0) {
      setActiveIndex(0);
    }
  }, [loading, projects]);

  const setItemRef = (el, i) => {
    itemRefs.current[i] = el;
  };

  const setSentinelRef = (el, i) => {
    sentinelRefs.current[i] = el;
  };

  // SVG Icons
  const PackageIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M16.5 9.4l-9-5.19"/>
      <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
      <polyline points="3.27 6.96 12 12.01 20.73 6.96"/>
      <line x1="12" y1="22.08" x2="12" y2="12"/>
    </svg>
  );

  const ArrowUpRightIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M7 7h10v10"/>
      <path d="M7 17L17 7"/>
    </svg>
  );

  if (loading) {
    return (
      <div style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontFamily: "system-ui, -apple-system, sans-serif"
      }}>
        <p style={{ fontSize: "1.5rem", color: "#666" }}>Loading projects...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontFamily: "system-ui, -apple-system, sans-serif"
      }}>
        <p style={{ fontSize: "1.5rem", color: "#ef4444" }}>Error: {error}</p>
      </div>
    );
  }

  return (
    <section style={{
      padding: "8rem 1.5rem",
      fontFamily: "system-ui, -apple-system, sans-serif",
      backgroundColor: "#fff",
      minHeight: "100vh"
    }}>
      <div style={{
        maxWidth: "1200px",
        margin: "0 auto"
      }}>
        {/* Back Link */}
        <a
          href="./"
          style={{
            fontSize: "1rem",
            textDecoration: "none",
            color: "#555",
            marginBottom: "2rem",
            display: "inline-block",
            cursor: "pointer"
          }}
          onMouseEnter={(e) => {
            e.target.style.textDecoration = "underline";
            e.target.style.color = "#000";
          }}
          onMouseLeave={(e) => {
            e.target.style.textDecoration = "none";
            e.target.style.color = "#555";
          }}
        >
          ← Back to Home
        </a>

        {/* Header */}
        <div style={{
          maxWidth: "768px",
          margin: "0 auto"
        }}>
          <h1 style={{
            marginBottom: "1rem",
            fontSize: "clamp(2rem, 5vw, 3rem)",
            fontWeight: "bold",
            letterSpacing: "-0.025em",
            fontFamily: "'Kaushan Script', cursive"
          }}>
            All My Projects
          </h1>
          <p style={{
            marginBottom: "1.5rem",
            fontSize: "clamp(1rem, 2vw, 1.125rem)",
            color: "#666",
            lineHeight: "1.6"
          }}>
            Explore my portfolio of work, showcasing design, development, and creative projects built with passion and precision.
          </p>
        </div>

        {/* Timeline */}
        <div style={{
          maxWidth: "768px",
          margin: "4rem auto 0",
          display: "flex",
          flexDirection: "column",
          gap: "clamp(4rem, 8vw, 6rem)",
          position: "relative"
        }}>
          {/* Vertical Line */}
          <div style={{
            position: "absolute",
            left: window.innerWidth >= 768 ? "1.75rem" : "1.25rem",
            top: "2rem",
            bottom: "2rem",
            width: "2px",
            backgroundColor: "#e5e7eb",
            zIndex: 0
          }}>
            {/* Animated progress line */}
            <div style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: `${((activeIndex + 1) / projects.length) * 100}%`,
              backgroundColor: "#000",
              transition: "height 0.5s ease-out",
              borderRadius: "999px"
            }} />
          </div>

          {projects.map((project, index) => {
            const isActive = index === activeIndex;

            return (
              <div
                key={project.id}
                ref={(el) => setItemRef(el, index)}
                style={{
                  position: "relative",
                  display: "flex",
                  flexDirection: window.innerWidth >= 768 ? "row" : "column",
                  gap: window.innerWidth >= 768 ? "4rem" : "1rem",
                  zIndex: 1
                }}
              >
                {/* Sticky meta column */}
                <div style={{
                  position: window.innerWidth >= 768 ? "sticky" : "static",
                  top: "2rem",
                  width: window.innerWidth >= 768 ? "16rem" : "100%",
                  flexShrink: 0,
                  height: "fit-content",
                  display: "flex",
                  alignItems: "center",
                  gap: "1rem",
                  paddingLeft: window.innerWidth >= 768 ? "0" : "3.5rem"
                }}>
                  <div style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.75rem"
                  }}>
                    <div style={{
                      padding: "0.5rem",
                      borderRadius: "0.5rem",
                      backgroundColor: isActive ? "#000" : "#f3f4f6",
                      color: isActive ? "#fff" : "#6b7280",
                      transition: "all 0.3s",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      position: window.innerWidth >= 768 ? "relative" : "absolute",
                      left: window.innerWidth >= 768 ? "auto" : "0",
                      zIndex: 2,
                      boxShadow: isActive ? "0 0 0 4px #fff" : "none"
                    }}>
                      <PackageIcon />
                    </div>
                    <div style={{
                      display: "flex",
                      flexDirection: "column"
                    }}>
                      <span style={{
                        fontSize: "0.875rem",
                        fontWeight: 500
                      }}>
                        {project.title}
                      </span>
                      <span style={{
                        fontSize: "0.75rem",
                        color: "#6b7280"
                      }}>
                        Project #{index + 1}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Sentinel for proximity detection */}
                <div
                  ref={(el) => setSentinelRef(el, index)}
                  style={{
                    position: "absolute",
                    top: "-6rem",
                    left: 0,
                    width: "3rem",
                    height: "3rem",
                    opacity: 0,
                    pointerEvents: "none"
                  }}
                />

                {/* Content column */}
                <article style={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  borderRadius: "1rem",
                  border: isActive ? "1px solid #e5e7eb" : "1px solid #e5e7eb",
                  padding: "0.75rem",
                  transition: "all 0.3s",
                  backgroundColor: isActive ? "#f9fafb" : "#f9fafb",
                  boxShadow: isActive ? "0 10px 25px rgba(0,0,0,0.1)" : "0 4px 12px rgba(0,0,0,0.05)"
                }}>
                  {/* Image */}
                  {project.media?.images?.[0] && (
                    <img
                      src={project.media.images[0]}
                      alt={project.title}
                      style={{
                        marginBottom: "1rem",
                        width: "100%",
                        height: "18rem",
                        borderRadius: "0.5rem",
                        objectFit: "cover"
                      }}
                      loading="lazy"
                    />
                  )}

                  <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
                    {/* Header */}
                    <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                      <h2 style={{
                        fontSize: window.innerWidth >= 768 ? "1.125rem" : "1rem",
                        fontWeight: 500,
                        lineHeight: "1.3",
                        letterSpacing: "-0.025em",
                        color: isActive ? "#000" : "rgba(0,0,0,0.7)",
                        transition: "color 0.2s"
                      }}>
                        {project.title}
                      </h2>

                      {/* Description */}
                      <p style={{
                        fontSize: window.innerWidth >= 768 ? "0.875rem" : "0.75rem",
                        lineHeight: "1.6",
                        color: isActive ? "#6b7280" : "rgba(107,114,128,0.8)",
                        transition: "all 0.3s",
                        display: "-webkit-box",
                        WebkitLineClamp: isActive ? "unset" : 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden"
                      }}>
                        {project.short_description || project.description || "No description available"}
                      </p>
                    </div>

                    {/* Expandable content */}
                    <div style={{
                      display: "grid",
                      gridTemplateRows: isActive ? "1fr" : "0fr",
                      opacity: isActive ? 1 : 0,
                      transition: "all 0.5s ease-out"
                    }}>
                      <div style={{ overflow: "hidden" }}>
                        <div style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "1rem",
                          paddingTop: "0.5rem"
                        }}>
                          {/* Full description if different from short */}
                          {project.description && project.description !== project.short_description && (
                            <div style={{
                              borderRadius: "0.5rem",
                              border: "1px solid #e5e7eb",
                              backgroundColor: "#f9fafb",
                              padding: "1rem"
                            }}>
                              <p style={{
                                fontSize: "0.875rem",
                                color: "#6b7280",
                                lineHeight: "1.6"
                              }}>
                                {project.description}
                              </p>
                            </div>
                          )}

                          {/* View Project Button */}
                          <div style={{ display: "flex", justifyContent: "flex-end" }}>
                            <a
                              href={`/projects/${project.id}`}
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "0.375rem",
                                padding: "0.5rem 0.75rem",
                                fontSize: "0.75rem",
                                fontWeight: 500,
                                backgroundColor: "#000",
                                color: "#fff",
                                borderRadius: "0.5rem",
                                textDecoration: "none",
                                transition: "all 0.2s",
                                boxShadow: "0 1px 2px rgba(0,0,0,0.05)"
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = "#333";
                                const svg = e.currentTarget.querySelector('svg');
                                if (svg) svg.style.transform = "translate(0.125rem, -0.125rem)";
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = "#000";
                                const svg = e.currentTarget.querySelector('svg');
                                if (svg) svg.style.transform = "translate(0, 0)";
                              }}
                            >
                              View Project
                              <span style={{ 
                                display: "inline-flex", 
                                transition: "transform 0.2s" 
                              }}>
                                <ArrowUpRightIcon />
                              </span>
                            </a>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </article>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default ProjectsTimeline;